import React, { Component } from 'react';
import { ClockIcon, UserIcon, MusicIcon } from 'lucide-react';
import { useTheme } from '../providers/ThemeProvider';
// Type definitions
export interface LessonChipProps {
  id: string;
  title: string;
  startTime: string; // Format: "HH:MM" (24-hour)
  endTime?: string; // Format: "HH:MM" (24-hour)
  studentName: string;
  instrument?: string;
  color?: string;
  status?: 'PENDING' | 'CONFIRMED' | 'COMPLETED' | 'CANCELED';
  variant?: 'compact' | 'detailed';
  onClick?: (id: string, event: React.MouseEvent) => void;
  onKeyDown?: (id: string, event: React.KeyboardEvent) => void;
  className?: string;
  isTemporary?: boolean; // For optimistic updates
}
// Helper function to format time from 24h to 12h format
const formatTime12h = (time24h: string): string => {
  if (!time24h) return '';
  const [hours, minutes] = time24h.split(':');
  const hour = parseInt(hours, 10);
  const suffix = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${hour12}:${minutes} ${suffix}`;
};
// Helper function to format time range
const formatTimeRange = (startTime: string, endTime?: string): string => {
  const start = formatTime12h(startTime);
  if (!endTime) return start;
  const end = formatTime12h(endTime);
  return `${start}–${end}`;
};
// Helper to determine if text should be light based on background color
const shouldUseLightText = (bgColor: string): boolean => {
  // Convert hex to RGB
  const hex = bgColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  // Calculate luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  // Return true for light text if background is dark
  // FIXED: Changed threshold from 0.7 to 0.5 for proper contrast
  // Light backgrounds (luminance > 0.5) should use dark text
  // Dark backgrounds (luminance < 0.5) should use light text
  return luminance < 0.5;
};
// Helper to get status color
const getStatusColor = (status?: string): string => {
  switch (status?.toUpperCase()) {
    case 'CONFIRMED':
      return '#10B981';
    // Green - matches the "Today" button and primary CTA
    // Green
    case 'PENDING':
      return '#EAB308';
    // Yellow
    case 'COMPLETED':
      return '#3B82F6';
    // Blue
    case 'CANCELED':
      return '#EF4444';
    // Red
    default:
      return '#6B7280';
    // Gray
  }
};
export const LessonChip: React.FC<LessonChipProps> = ({
  id,
  title,
  startTime,
  endTime,
  studentName,
  instrument,
  color = '#6B7280',
  status = 'CONFIRMED',
  variant = 'compact',
  onClick,
  onKeyDown,
  className = '',
  isTemporary = false
}) => {
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  // Determine text color based on background
  const textColor = shouldUseLightText(color) ? 'text-white' : 'text-gray-900';
  // Handle click
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onClick) {
      onClick(id, e);
    }
  };
  // Handle keyboard events
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      if (onKeyDown) {
        onKeyDown(id, e);
      } else if (onClick) {
        onClick(id, e as any);
      }
    }
  };
  // Format the time display
  const timeDisplay = formatTimeRange(startTime, endTime);
  // Status indicator styling
  const statusColor = getStatusColor(status);
  // Base classes for both variants
  const baseClasses = `
    lesson-chip-wrapper
    relative rounded-lg transition-transform duration-150 cursor-pointer
    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-deep-pink
    hover:translate-y-[-1px] hover:shadow-md
    ${textColor}
    ${isTemporary ? 'ring-2 ring-white shadow-lg animate-pulse' : 'shadow-sm'}
    ${className}
  `;
  // Compact variant (for month view)
  if (variant === 'compact') {
    return <div className={`${baseClasses} px-2 py-1 text-xs font-medium flex items-center`} style={{
      backgroundColor: color,
      willChange: 'transform'
    }} onClick={handleClick} onKeyDown={handleKeyDown} tabIndex={0} role="button" aria-label={`Lesson: ${title} with ${studentName} at ${timeDisplay}`} data-id="element-630">
        <div className="flex items-center gap-1 min-w-0 flex-1" data-id="element-631">
          <ClockIcon className="h-3 w-3 flex-shrink-0" aria-hidden="true" data-id="element-632" />
          {/* Hide time text on mobile, show on desktop */}
          <span className="hidden sm:inline whitespace-nowrap flex-shrink-0" data-id="element-633">
            {formatTime12h(startTime)}
          </span>
          <span className="hidden sm:inline mx-0.5 flex-shrink-0" aria-hidden="true" data-id="element-634">
            •
          </span>
          <span className="truncate min-w-0" data-id="element-635">{studentName}</span>
        </div>
        {/* Status indicator dot */}
        <div className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full" style={{
        backgroundColor: statusColor
      }} aria-label={`Status: ${status}`} data-id="element-636" />
      </div>;
  }
  // Detailed variant (for week view)
  return <div className={`${baseClasses} px-3 py-2 text-sm flex flex-col justify-center`} style={{
    backgroundColor: color,
    willChange: 'transform'
  }} onClick={handleClick} onKeyDown={handleKeyDown} tabIndex={0} role="button" aria-label={`Lesson: ${title} with ${studentName} at ${timeDisplay}`} data-id="element-637">
      {/* Time range */}
      <div className="flex items-center gap-1.5 mb-1 overflow-hidden" data-id="element-638">
        <ClockIcon className="h-4 w-4 flex-shrink-0" aria-hidden="true" data-id="element-639" />
        <span className="font-semibold truncate" data-id="element-640">{timeDisplay}</span>
      </div>
      {/* Title */}
      <div className="font-medium mb-1 truncate" data-id="element-641">{title}</div>
      {/* Student info */}
      <div className="flex items-center gap-1.5 mb-1 overflow-hidden" data-id="element-642">
        <UserIcon className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" data-id="element-643" />
        <span className="text-xs truncate" data-id="element-644">{studentName}</span>
      </div>
      {/* Instrument (if provided) */}
      {instrument && <div className="flex items-center gap-1.5 overflow-hidden" data-id="element-645">
          <MusicIcon className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" data-id="element-646" />
          <span className="text-xs truncate" data-id="element-647">{instrument}</span>
        </div>}
      {/* Status badge */}
      <div className="absolute top-2 right-2 flex items-center gap-1" data-id="element-648">
        <div className="w-2 h-2 rounded-full" style={{
        backgroundColor: statusColor
      }} aria-label={`Status: ${status}`} data-id="element-649" />
      </div>
    </div>;
};
// Mock examples for testing
export const MockLessonChips = () => {
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const mockLessons: LessonChipProps[] = [{
    id: '1',
    title: 'Piano Lesson',
    startTime: '09:00',
    endTime: '09:45',
    studentName: 'Emma Johnson',
    instrument: 'Piano',
    color: '#4FC3F7',
    status: 'CONFIRMED'
  }, {
    id: '2',
    title: 'Guitar Practice',
    startTime: '14:30',
    endTime: '15:30',
    studentName: 'Alex Smith',
    instrument: 'Guitar',
    color: '#81C784',
    status: 'PENDING'
  }, {
    id: '3',
    title: 'Violin Lesson',
    startTime: '16:00',
    endTime: '16:45',
    studentName: 'Sophia Martinez',
    instrument: 'Violin',
    color: '#FFD54F',
    status: 'COMPLETED'
  }, {
    id: '4',
    title: 'Voice Training',
    startTime: '11:00',
    endTime: '12:00',
    studentName: 'Michael Brown',
    instrument: 'Voice',
    color: '#F48FB1',
    status: 'CANCELED'
  }, {
    id: '5',
    title: 'Drum Session',
    startTime: '13:00',
    studentName: 'Chris Davis',
    instrument: 'Drums',
    color: '#BA68C8',
    status: 'CONFIRMED',
    isTemporary: true
  }];
  const handleClick = (id: string) => {
    console.log('Clicked lesson:', id);
  };
  return <div className={`p-8 ${isDark ? 'bg-navy-900' : 'bg-gray-50'} min-h-screen`} data-id="element-650">
      <div className="max-w-6xl mx-auto space-y-8" data-id="element-651">
        {/* Header */}
        <div className={`${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-652">
          <h1 className="text-3xl font-bold mb-2" data-id="element-653">
            LessonChip Component Examples
          </h1>
          <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-654">
            Interactive examples showing compact and detailed variants
          </p>
        </div>
        {/* Compact Variant Examples */}
        <section className={`${isDark ? 'bg-white/5' : 'bg-white'} rounded-xl p-6 border ${isDark ? 'border-white/10' : 'border-gray-200'}`} data-id="element-655">
          <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-656">
            Compact Variant (Month View)
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-657">
            {mockLessons.map(lesson => <LessonChip key={lesson.id} {...lesson} variant="compact" onClick={handleClick} data-id="element-658" />)}
          </div>
          <p className={`mt-4 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-659">
            Compact chips show time and student name. Hover to see elevation
            effect.
          </p>
        </section>
        {/* Detailed Variant Examples */}
        <section className={`${isDark ? 'bg-white/5' : 'bg-white'} rounded-xl p-6 border ${isDark ? 'border-white/10' : 'border-gray-200'}`} data-id="element-660">
          <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-661">
            Detailed Variant (Week View)
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-662">
            {mockLessons.map(lesson => <LessonChip key={lesson.id} {...lesson} variant="detailed" onClick={handleClick} data-id="element-663" />)}
          </div>
          <p className={`mt-4 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-664">
            Detailed chips show full information including time range, title,
            student, and instrument.
          </p>
        </section>
        {/* Accessibility Info */}
        <section className={`${isDark ? 'bg-white/5' : 'bg-white'} rounded-xl p-6 border ${isDark ? 'border-white/10' : 'border-gray-200'}`} data-id="element-665">
          <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-666">
            Accessibility Features
          </h2>
          <ul className={`space-y-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-667">
            <li className="flex items-start" data-id="element-668">
              <span className="mr-2" data-id="element-669">✓</span>
              <span data-id="element-670">
                <strong data-id="element-671">Keyboard Navigation:</strong> All chips are focusable
                and can be activated with Enter or Space keys
              </span>
            </li>
            <li className="flex items-start" data-id="element-672">
              <span className="mr-2" data-id="element-673">✓</span>
              <span data-id="element-674">
                <strong data-id="element-675">ARIA Labels:</strong> Screen readers announce lesson
                details, student name, time, and status
              </span>
            </li>
            <li className="flex items-start" data-id="element-676">
              <span className="mr-2" data-id="element-677">✓</span>
              <span data-id="element-678">
                <strong data-id="element-679">Focus Indicators:</strong> Clear visual focus ring
                appears when navigating with keyboard
              </span>
            </li>
            <li className="flex items-start" data-id="element-680">
              <span className="mr-2" data-id="element-681">✓</span>
              <span data-id="element-682">
                <strong data-id="element-683">Color Contrast:</strong> Text color automatically
                adjusts based on background for optimal readability
              </span>
            </li>
            <li className="flex items-start" data-id="element-684">
              <span className="mr-2" data-id="element-685">✓</span>
              <span data-id="element-686">
                <strong data-id="element-687">Status Indicators:</strong> Visual dots with ARIA labels
                communicate lesson status
              </span>
            </li>
            <li className="flex items-start" data-id="element-688">
              <span className="mr-2" data-id="element-689">✓</span>
              <span data-id="element-690">
                <strong data-id="element-691">Hover Effects:</strong> Subtle elevation and shadow
                changes provide visual feedback
              </span>
            </li>
          </ul>
        </section>
        {/* Usage Example */}
        <section className={`${isDark ? 'bg-white/5' : 'bg-white'} rounded-xl p-6 border ${isDark ? 'border-white/10' : 'border-gray-200'}`} data-id="element-692">
          <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-693">
            Usage Example
          </h2>
          <pre className={`${isDark ? 'bg-navy-800' : 'bg-gray-100'} p-4 rounded-lg overflow-x-auto text-sm ${isDark ? 'text-gray-300' : 'text-gray-800'}`} data-id="element-694">
            {`import { LessonChip } from './components/calendar/LessonChip'
// Compact variant (month view)
<LessonChip
  id="lesson-1"
  title="Piano Lesson"
  startTime="09:00"
  endTime="09:45"
  studentName="Emma Johnson"
  instrument="Piano"
  color="#4FC3F7"
  status="CONFIRMED"
  variant="compact"
  onClick={(id) => console.log('Clicked:', id)}
/>
// Detailed variant (week view)
<LessonChip
  id="lesson-1"
  title="Piano Lesson"
  startTime="09:00"
  endTime="09:45"
  studentName="Emma Johnson"
  instrument="Piano"
  color="#4FC3F7"
  status="CONFIRMED"
  variant="detailed"
  onClick={(id) => console.log('Clicked:', id)}
/>`}
          </pre>
        </section>
      </div>
    </div>;
};